import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { LanguageProvider } from './context/LanguageContext';
import { MyAuthProvider } from './context/MyAuthContext';
import AdminBar from './components/AdminBar';
import Navbar from './components/Navbar';
import PersonaSwitcher from './components/PersonaSwitcher';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import MatomoTracker from './components/MatomoTracker';
import VacationBanner from './components/VacationBanner';
import PageTransition from './components/motion/PageTransition';
import Home from './pages/Home';
import Solution from './pages/Solution';
import Company from './pages/Company';
import Contact from './pages/Contact';
import News from './pages/News';
import NewsArticle from './pages/NewsArticle';
import AdminNews from './pages/AdminNews';
import LegalMentions from './pages/LegalMentions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiesPage from './pages/CookiesPage';
import GoogleAppsPage from './pages/GoogleAppsPage';
import Horaires from './pages/Horaires';
import ApporteurAffaires from './pages/ApporteurAffaires';
import Leads from './pages/Leads';
import DevisPC from './pages/DevisPC';
import PrendreRdv from './pages/PrendreRdv';
import Configurateur from './pages/Configurateur';
import AnnulerRdv from './pages/AnnulerRdv';
import CgvRdv from './pages/CgvRdv';
import ExpertiseLocale from './pages/ExpertiseLocale';
import NotFound from './pages/NotFound';
// Back-office centralisé /my
import MyLogin from './pages/my/Login';
import MyDashboard from './pages/my/Dashboard';
import MyUsers from './pages/my/Users';
import MyNews from './pages/my/News';
import MyBoutique from './pages/my/Boutique';
import MyBoutiqueCallback from './pages/my/BoutiqueCallback';
import MySites from './pages/my/Sites';
import RequireAuth from './pages/my/RequireAuth';
import {
  COOKIE_CONSENT_ACCEPTED,
  COOKIE_CONSENT_REJECTED,
  getCookieConsent,
  saveCookieConsent,
} from './lib/cookieConsent';

function PublicSite() {
  const [cookieState, setCookieState] = useState(() => {
    const storedConsent = getCookieConsent();
    return {
      consent: storedConsent,
      bannerOpen: storedConsent === null,
    };
  });

  const openCookieBanner = () => {
    setCookieState((current) => ({
      ...current,
      bannerOpen: true,
    }));
  };

  const closeCookieBanner = () => {
    setCookieState((current) => (
      current.consent === null
        ? current
        : { ...current, bannerOpen: false }
    ));
  };

  const updateCookieConsent = (consent) => {
    saveCookieConsent(consent);

    setCookieState({
      consent,
      bannerOpen: false,
    });
  };

  return (
    <>
      <MatomoTracker consent={cookieState.consent} />
      <div className="noise-overlay" />
      <PersonaSwitcher currentSite="meteris" />
      <Navbar />
      <VacationBanner />
      <PageTransition>
        {(location) => (
      <Routes location={location}>
        <Route path="/" element={<Home />} />
        <Route path="/solutions/:slug" element={<Solution />} />
        <Route path="/entreprise" element={<Company />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/actualites" element={<News />} />
        <Route path="/actualites/:slug" element={<NewsArticle />} />
        <Route path="/admin/news" element={<RequireAuth minRole="editeur"><AdminNews /></RequireAuth>} />
        <Route path="/mentions-legales" element={<LegalMentions />} />
        <Route path="/politique-confidentialite" element={<PrivacyPolicy />} />
        <Route path="/cookies" element={<CookiesPage onManageCookies={openCookieBanner} />} />
        <Route path="/google-app" element={<GoogleAppsPage />} />
        <Route path="/google-apps" element={<GoogleAppsPage />} />
        <Route path="/nos-horaires" element={<Horaires />} />
        <Route path="/devenir-apporteur-daffaires" element={<ApporteurAffaires />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/devis-pc" element={<DevisPC />} />
        <Route path="/prendre-rdv" element={<PrendreRdv />} />
        <Route path="/configurateur" element={<Configurateur />} />
        <Route path="/prendre-rdv/annuler" element={<AnnulerRdv />} />
        <Route path="/cgv-rdv" element={<CgvRdv />} />
        <Route path="/expertise-locale/:ville" element={<ExpertiseLocale />} />
        {/* Toute autre adresse : page introuvable (pré-rendue sur /404, servie en vrai 404 par nginx) */}
        <Route path="*" element={<NotFound />} />
      </Routes>
        )}
      </PageTransition>
      <Footer onManageCookies={openCookieBanner} />
      <CookieBanner
        consent={cookieState.consent}
        isOpen={cookieState.bannerOpen}
        onAccept={() => updateCookieConsent(COOKIE_CONSENT_ACCEPTED)}
        onReject={() => updateCookieConsent(COOKIE_CONSENT_REJECTED)}
        onClose={closeCookieBanner}
      />
    </>
  );
}

export default function App() {
  // Lève la garde posée par public/index.html : le texte du hero reste invisible jusqu'à ce que
  // React ait pris la main (sinon le HTML pré-rendu s'affiche, disparaît, puis rejoue son ouverture).
  useEffect(() => {
    document.documentElement.classList.remove('pre-hydrate');
  }, []);

  return (
    <LanguageProvider>
      <MotionConfig reducedMotion="user">
      <Router>
        <MyAuthProvider>
          <Routes>
            {/* ── Back-office centralisé ── */}
            <Route path="/my" element={<MyLogin />} />
            <Route path="/my/dashboard" element={
              <RequireAuth minRole="client"><MyDashboard /></RequireAuth>
            } />
            <Route path="/my/actualites" element={
              <RequireAuth minRole="editeur"><MyNews /></RequireAuth>
            } />
            <Route path="/my/boutique" element={
              <RequireAuth minRole="editeur"><MyBoutique /></RequireAuth>
            } />
            <Route path="/my/boutique/callback" element={
              <RequireAuth minRole="editeur"><MyBoutiqueCallback /></RequireAuth>
            } />
            <Route path="/my/sites" element={
              <RequireAuth minRole="editeur"><MySites /></RequireAuth>
            } />
            <Route path="/my/utilisateurs" element={
              <RequireAuth minRole="admin"><MyUsers /></RequireAuth>
            } />

            {/* ── Site public ── */}
            <Route path="/*" element={<PublicSite />} />
          </Routes>
          {/* Barre admin flottante (visible uniquement pour les superadmins identifiés) */}
          <AdminBar />
        </MyAuthProvider>
      </Router>
      </MotionConfig>
    </LanguageProvider>
  );
}
